/**
 * 페이지 선택 컴포넌트
 * 학생이 풀 페이지를 선택하는 UI
 */

import React, { useState } from 'react';
import { PixelButton } from '../common/PixelButton';
import { PixelInput } from '../common/PixelInput';

/**
 * PageSelector Props
 */
interface PageSelectorProps {
  /** 총 페이지 수 */
  totalPages: number;
  /** 페이지 선택 콜백 */
  onSelectPage: (page: number) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * PageSelector 컴포넌트
 *
 * - 숫자 입력 또는 버튼 선택
 * - 페이지 범위 검증
 * - 레트로 게임 스타일
 */
export const PageSelector: React.FC<PageSelectorProps> = ({
  totalPages,
  onSelectPage,
  disabled = false,
}) => {
  const [selectedPage, setSelectedPage] = useState<string>('');
  const [error, setError] = useState<string>('');

  /**
   * 페이지 번호 입력 핸들러
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedPage(value);
    setError('');
  };

  /**
   * 페이지 선택 제출
   */
  const handleSubmit = () => {
    if (disabled) return;

    const pageNum = parseInt(selectedPage);

    // 유효성 검증
    if (isNaN(pageNum)) {
      setError('숫자를 입력해주세요');
      return;
    }

    // totalPages가 0이면 제한 없음
    if (totalPages > 0 && (pageNum < 1 || pageNum > totalPages)) {
      setError(`1~${totalPages} 사이의 페이지를 입력하세요`);
      return;
    }

    if (pageNum < 1) {
      setError('1 이상의 페이지를 입력하세요');
      return;
    }

    onSelectPage(pageNum);
  };

  return (
    <div className="space-y-4">
      {/* 안내 메시지 */}
      <p className="text-center font-pixel text-sm text-gray-400">
        몇 페이지를 풀었어?
      </p>

      {/* 페이지 번호 입력 */}
      <div className="space-y-2">
        <PixelInput
          type="number"
          placeholder="페이지 번호"
          value={selectedPage}
          onChange={handleInputChange}
          error={error}
          disabled={disabled}
          min={1}
          max={totalPages > 0 ? totalPages : 999}
        />

        {/* 제출 버튼 */}
        <PixelButton
          variant="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={!selectedPage || disabled}
          className="w-full"
        >
          {selectedPage ? `${selectedPage}페이지 시작!` : '페이지를 입력하세요'}
        </PixelButton>
      </div>
    </div>
  );
};
